import React from 'react';
import { Card, Button, Icon, TextInput, SelectInput, LoadingOverlay, Spinner } from '../../components/ui';
import SetupNavHead from './SetupNavHead';

/*
 * FlatList — the left pane for flat (non-hierarchy) Setup entities
 * (Users/Guards, Assignments, Companies, Plates, Drivers). Presentational:
 * a header with an "Add" button, a search box, an optional filter toolbar, and
 * a scrollable list of rows. Selection + data + row rendering are supplied by
 * the parent entity component (which owns the Redux list + CRUD).
 *
 * Two search modes:
 *   - client (default): FlatList owns the search text and filters `items`
 *     locally via `filterKeys` (Users/Guards, Assignments — full list in memory).
 *   - server (opt-in): pass `onSearchChange` (+ `searchValue`) and FlatList
 *     becomes a controlled search box that reports keystrokes up; the parent
 *     re-fetches and passes back the already-filtered `items`. Combine with
 *     `onScrollEnd`/`loadingMore` for infinite scroll (Companies/Plates/Drivers).
 *
 * Props:
 *   title       : eyebrow label (e.g. "Users & Guards")
 *   items       : array of row objects (from Redux)
 *   getKey      : (row) => key
 *   getPrimary  : (row) => primary line text
 *   getSecondary: (row) => secondary/meta line text (optional)
 *   getBadges   : (row) => array of { label, on } minipills (optional)
 *   selectedKey : currently-selected row key
 *   onSelect    : (row) => void
 *   onAdd       : () => void   (omit to hide Add)
 *   addLabel    : Add button text
 *   loading     : bool — overlay veils the list (initial load / filter change)
 *   filterKeys  : array of (row)=>string used for the CLIENT-side search
 *   categories  : optional [{label, value}] — type-filter chips shown above search
 *   getCategory : (row) => value — matched against the selected category chip
 *   footer      : optional node rendered in the list footer (e.g. Done)
 *   emptyText   : text when there are no items
 *   searchValue      : controlled search text (server mode)
 *   onSearchChange   : (value)=>void — presence switches to server mode
 *   searchPlaceholder: placeholder for the search box (default "Search…")
 *   toolbar          : optional node rendered under the search (filter selects)
 *   onScrollEnd      : ()=>void — fired near the scroll bottom (infinite scroll)
 *   loadingMore      : bool — show a spinner row at the list bottom
 */
export default function FlatList({
  title,
  items = [],
  getKey,
  getPrimary,
  getSecondary,
  getBadges,
  selectedKey,
  onSelect,
  onAdd,
  onBack,
  addLabel = 'Add',
  loading = false,
  filterKeys = [],
  categories = [],
  getCategory,
  groupBy,
  footer,
  emptyText = 'No records.',
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  toolbar,
  onScrollEnd,
  loadingMore = false,
}) {
  const serverMode = typeof onSearchChange === 'function';
  const [qInternal, setQInternal] = React.useState('');
  const q = serverMode ? (searchValue || '') : qInternal;
  const setQ = serverMode ? onSearchChange : setQInternal;
  const [cat, setCat] = React.useState('');
  const [collapsed, setCollapsed] = React.useState(() => new Set());
  const toggleGroup = (label) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });
  const query = q.trim().toLowerCase();

  // category filter first, then the text search narrows the filtered set.
  const byCat = (cat === '' || !getCategory)
    ? items
    : items.filter((row) => String(getCategory(row)) === String(cat));
  // server mode: the list is already filtered upstream — render it verbatim.
  const filtered = serverMode
    ? items
    : (!query
        ? byCat
        : byCat.filter((row) =>
            filterKeys.some((fn) => {
              const v = fn(row);
              return v != null && String(v).toLowerCase().includes(query);
            })
          ));
  const catCount = (value) => value === ''
    ? items.length
    : (getCategory ? items.filter((row) => String(getCategory(row)) === String(value)).length : 0);

  const scrollRef = React.useRef(null);
  const handleScroll = (e) => {
    if (!onScrollEnd) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 56) onScrollEnd();
  };
  // Auto-fill: if the loaded rows don't overflow the pane there's no scrollbar to
  // trigger load-more, so keep requesting pages until the list overflows (or the
  // parent's onScrollEnd becomes a no-op once there are no more pages).
  React.useEffect(() => {
    if (!onScrollEnd || loading || loadingMore) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) onScrollEnd();
  }, [onScrollEnd, loading, loadingMore, filtered.length]);

  const renderRow = (row) => {
    const key = getKey(row);
    const badges = getBadges ? getBadges(row) : null;
    return (
      <div
        key={key}
        className={`setuprow${key === selectedKey ? ' is-active' : ''}`}
        onClick={() => onSelect(row)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row); } }}
        tabIndex={0}
        role="button"
        aria-pressed={key === selectedKey}
      >
        <div className="setuprow__main">
          <div className="setuprow__name truncate">{getPrimary(row)}</div>
          {getSecondary && <div className="setuprow__meta truncate">{getSecondary(row)}</div>}
        </div>
        {badges && badges.length > 0 && (
          <div className="setuprow__badges">
            {badges.map((b, i) => <span key={i} className={`minipill${b.on ? ' on' : ''}${b.warn ? ' minipill--warn' : ''}`}>{b.label}</span>)}
          </div>
        )}
      </div>
    );
  };

  // optional grouping: bucket filtered rows by groupBy(row) → [{label, rows}], sorted
  // alphabetically. Rendered flat when 0–1 group (a single-customer admin shouldn't get
  // a redundant header).
  let groups = null;
  if (typeof groupBy === 'function' && filtered.length > 0) {
    const map = new Map();
    filtered.forEach((row) => {
      const label = groupBy(row) || '—';
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(row);
    });
    if (map.size > 1) {
      groups = Array.from(map.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, rows]) => ({ label, rows }));
    }
  }

  return (
    <div className="setup__nav">
      <Card flush style={{ position: 'relative' }}>
        <LoadingOverlay show={loading} label="Loading" />
        <SetupNavHead title={title} onBack={onBack}>
          {onAdd && <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={onAdd}>{addLabel}</Button>}
        </SetupNavHead>
        <div className="setup__search">
          <TextInput
            search
            size="sm"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={`Search ${title}`}
          />
        </div>
        {categories && categories.length > 1 && getCategory && (
          <div className="setup__toolbar">
            <SelectInput size="sm" value={cat} onChange={(e) => setCat(e.target.value)} aria-label={`Filter ${title} by type`}>
              {categories.map((c) => (
                <option key={String(c.value)} value={c.value}>{c.label} ({catCount(c.value)})</option>
              ))}
            </SelectInput>
          </div>
        )}
        {toolbar && <div className="setup__toolbar">{toolbar}</div>}
        <div className="setup__scroll" ref={scrollRef} onScroll={onScrollEnd ? handleScroll : undefined}>
          {filtered.length === 0 ? (
            <div className="sub" style={{ padding: 16, textAlign: 'center' }}>{emptyText}</div>
          ) : groups ? (
            groups.map((g) => {
              // during an active search, force every matching group open so the found
              // record shows without hand-expanding a collapsed group (mirrors the org
              // tree's search-expands-to-match behavior). `query` is '' when not searching.
              const isCollapsed = !query && collapsed.has(g.label);
              return (
                <div className="setup__group" key={g.label}>
                  <button type="button" className="setup__grouphdr" onClick={() => toggleGroup(g.label)} aria-expanded={!isCollapsed}>
                    <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={13} />
                    <span className="truncate">{g.label}</span>
                    <span className="setup__groupn">{g.rows.length}</span>
                  </button>
                  {!isCollapsed && g.rows.map(renderRow)}
                </div>
              );
            })
          ) : (
            filtered.map(renderRow)
          )}
          {loadingMore && (
            <div className="setup__more">
              <Spinner size={15} /> <span className="sub">Loading more…</span>
            </div>
          )}
        </div>
        {footer && <div className="setup__navfoot">{footer}</div>}
      </Card>
    </div>
  );
}
